import React, { useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const EnhancedModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  size = 'medium',
  closeOnEscape = true,
  closeOnOutsideClick = true,
  showCloseButton = true,
  className = ''
}) => {
  const modalRef = useRef(null);
  const contentRef = useRef(null);
  
  const sizeClasses = {
    small: 'max-w-sm',
    medium: 'max-w-lg',
    large: 'max-w-2xl',
    xlarge: 'max-w-4xl',
    full: 'max-w-full mx-4'
  };

  // Memoized close handler
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, closeOnEscape, handleClose]);

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);

  // Handle outside click
  const handleBackdropClick = useCallback((event) => {
    if (!closeOnOutsideClick) return;
    
    // Check if click is on the backdrop (not on the modal content)
    if (modalRef.current === event.target) {
      handleClose();
    }
  }, [closeOnOutsideClick, handleClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50"
            aria-hidden="true"
          />

          {/* Modal Container */}
          <div 
            ref={modalRef}
            className="fixed inset-0 overflow-y-auto"
            onClick={handleBackdropClick}
          >
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <motion.div
                ref={contentRef}
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ 
                  duration: 0.2,
                  ease: [0.4, 0, 0.2, 1]
                }}
                className={`relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all ${sizeClasses[size]} w-full ${className}`}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header with title and close button */}
                <div className={`flex items-center justify-between ${title ? 'border-b border-gray-200' : ''} px-6 py-4`}>
                  {title && (
                    <h3 className="text-lg font-semibold leading-6 text-gray-900">
                      {title}
                    </h3>
                  )}
                  {!title && <div />} {/* Empty div for spacing when no title */}
                  {showCloseButton && (
                    <button
                      type="button"
                      className="ml-auto rounded-full p-1 bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                      onClick={handleClose}
                      aria-label="Close modal"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {/* Content */}
                <div className="max-h-[calc(100vh-200px)] overflow-y-auto px-6 py-4">
                  {children}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default EnhancedModal;